import { useState, useMemo } from 'react'
import { Sheet } from './UI'
import { Clapboard } from './ClapboardIcon'
import { nextProgressAfter, compareProgress } from '../lib/episode'

// ── PlansCheckIn ──────────────────────────────────────────────────────────────
// ONE consolidated "did you watch your plans?" sheet, replacing the old flow that
// fired a separate full-screen check-in per pending plan (7 plans → 7 popups).
// Lists tonight's episodes grouped by show with a checkbox each (default off — we
// never assume you watched), plus a per-show "all" toggle.
//
// On confirm, progress advances SAFELY: for each show we only advance to the
// furthest episode that is contiguous from your current progress, so checking a
// later episode can't silently mark the ones below it watched. Checked plans are
// settled (confirmed) so they stop nagging; unchecked plans are left pending and
// simply roll forward.

const key = (p) => `${p.season}-${p.episode}`

export function PlansCheckIn({ plans, user, wl, planCtx, onClose }) {
  const groups = useMemo(() => {
    const m = new Map()
    for (const p of plans) {
      if (!m.has(p.show_id)) m.set(p.show_id, { show_id: p.show_id, show_data: p.show_data, eps: [] })
      m.get(p.show_id).eps.push(p)
    }
    for (const g of m.values()) g.eps.sort((a, b) => a.season - b.season || a.episode - b.episode)
    return [...m.values()]
  }, [plans])

  const [checked, setChecked] = useState(() => new Set())
  const [busy, setBusy] = useState(false)

  const toggle = (id) => setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const allIds = plans.map(p => p.id)
  const allOn  = allIds.length > 0 && allIds.every(id => checked.has(id))
  const markAll = () => setChecked(allOn ? new Set() : new Set(allIds))
  const groupAll = (g) => {
    const ids = g.eps.map(p => p.id)
    const on = ids.every(id => checked.has(id))
    setChecked(prev => { const n = new Set(prev); ids.forEach(id => on ? n.delete(id) : n.add(id)); return n })
  }

  const handleDone = async () => {
    if (busy) return
    setBusy(true)
    try {
      for (const g of groups) {
        const seasons = (wl?.items || []).find(s => s.id === g.show_id || s.id === +g.show_id)?.seasons
        const prog = wl?.progress?.[g.show_id] || { season: g.eps[0].season, episode: g.eps[0].episode }
        const checkedEps = g.eps.filter(p => checked.has(p.id))
          .map(p => ({ season: p.season, episode: p.episode }))
          .sort((a, b) => a.season - b.season || a.episode - b.episode)
        // Advance only along the contiguous run starting at current progress.
        let expected = { season: prog.season, episode: prog.episode }
        let last = null
        for (const ep of checkedEps) {
          const cmp = compareProgress(ep, expected)
          if (cmp < 0) continue                 // already behind progress, ignore
          if (cmp === 0) { last = ep; expected = nextProgressAfter(ep, seasons); continue }
          break                                  // gap, don't jump past unwatched
        }
        if (last && wl?.saveProgress) {
          wl.saveProgress(g.show_id, nextProgressAfter(last, seasons), { watchedEp: last })
        }
      }
      // Settle every checked plan's status so it can't re-nag (one batched query).
      const checkedPlanIds = allIds.filter(id => checked.has(id))
      if (checkedPlanIds.length) await planCtx.confirmMany(checkedPlanIds)
      await planCtx.reload?.()
    } finally {
      onClose?.()
    }
  }

  const total = plans.length
  const nChecked = checked.size
  const one = total === 1
  const ROW = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'left', width: '100%', transition: 'background 160ms, border-color 160ms' }

  return (
    <Sheet open onClose={onClose} label={one ? 'Did you watch it?' : 'Did you watch them?'} from="bottom" width={480}
      rootProps={{ 'data-dynamic': '' }}
      panelStyle={{ padding: '0 0 max(18px, env(safe-area-inset-bottom))', overflow: 'hidden', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
      {/* Film-strip edge: the Writers' Room motif. */}
      <div aria-hidden="true" style={{ height: 10, flexShrink: 0, background: 'repeating-linear-gradient(90deg, rgba(var(--amber-rgb),0.55) 0 10px, transparent 10px 20px)', opacity: 0.55, maskImage: 'linear-gradient(to bottom, #000 55%, transparent)', WebkitMaskImage: 'linear-gradient(to bottom, #000 55%, transparent)' }} />
      <div style={{ padding: '14px 18px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--amber)' }}>🎬 Tonight’s plans · {total} episode{one ? '' : 's'}</div>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 500, lineHeight: 1.1, letterSpacing: '-0.01em', color: 'var(--text-strong)', marginTop: 6, textWrap: 'balance' }}>{one ? 'Did you watch it?' : 'Did you watch them?'}</div>
            <div style={{ color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.45, marginTop: 6 }}>Tap the ones you finished. Nothing is marked until you say so.</div>
          </div>
          {total > 1 && (
            <button onClick={markAll} aria-pressed={allOn} className="ui-hit" style={{ flexShrink: 0, marginTop: 2, height: 32, padding: '0 12px', borderRadius: 'var(--radius-pill)', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 12.5, fontWeight: 700, background: allOn ? 'var(--amber)' : 'rgba(var(--amber-rgb),0.10)', border: `1px solid rgba(var(--amber-rgb),${allOn ? 1 : 0.35})`, color: allOn ? 'var(--amber-ink)' : 'var(--amber)', transition: 'all 160ms' }}>{allOn ? 'All ✓' : 'All of them'}</button>
          )}
        </div>
      </div>

      <div style={{ overflowY: 'auto', padding: '16px 18px 4px', display: 'flex', flexDirection: 'column', gap: 14, scrollbarWidth: 'none' }}>
        {groups.map(g => (
          <div key={g.show_id} style={{ display: 'flex', gap: 12 }}>
            <div style={{ width: 44, flexShrink: 0 }}>
              <div style={{ width: 44, height: 66, borderRadius: 8, overflow: 'hidden', background: 'var(--surface)', border: '1px solid rgba(var(--ink-rgb),0.10)', boxShadow: '0 8px 20px rgba(0,0,0,0.4)' }}>
                {g.show_data?.poster_path && <img src={`https://image.tmdb.org/t/p/w154${g.show_data.poster_path}`} alt="" draggable="false" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <span style={{ fontFamily: 'var(--font-serif)', fontSize: 17, color: 'var(--text-strong)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.show_data?.name || 'Show'}</span>
                {g.eps.length > 1 && <button onClick={() => groupAll(g)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer' }}>all</button>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {g.eps.map(p => {
                  const on = checked.has(p.id)
                  return (
                    <button key={p.id} onClick={() => toggle(p.id)} aria-pressed={on} style={{ ...ROW, background: on ? 'rgba(var(--amber-rgb),0.12)' : 'var(--surface)', border: `1px solid ${on ? 'rgba(var(--amber-rgb),0.5)' : 'var(--border)'}` }}>
                      <span style={{ width: 30, height: 30, borderRadius: 'var(--radius-full)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? 'var(--amber)' : 'transparent', border: `1.5px solid ${on ? 'var(--amber)' : 'rgba(var(--ink-rgb),0.18)'}`, transition: 'all 160ms' }}>
                        <Clapboard size={17} watched={on} accent={on ? 'var(--amber-ink)' : 'var(--amber)'} idle="rgba(var(--ink-rgb),0.45)" centered />
                      </span>
                      <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', color: on ? 'var(--amber)' : 'var(--text-dim)' }}>S{p.season} · E{p.episode}</span>
                        {p.episode_name && <span style={{ color: 'var(--text)', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.episode_name}</span>}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '14px 18px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={busy} style={{ flex: nChecked > 0 ? '0 0 auto' : 1, padding: '0 18px', height: 48, borderRadius: 'var(--radius-pill)', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontWeight: 600, fontSize: 14.5, cursor: 'pointer', fontFamily: 'var(--font)' }}>{nChecked > 0 ? 'Not yet' : 'Not tonight, save them'}</button>
          {nChecked > 0 && (
            <button onClick={handleDone} disabled={busy} style={{ flex: 1, height: 48, borderRadius: 'var(--radius-pill)', border: 'none', cursor: busy ? 'default' : 'pointer', fontFamily: 'var(--font)', background: 'var(--amber)', color: 'var(--amber-ink)', fontWeight: 700, fontSize: 15, opacity: busy ? 0.7 : 1, boxShadow: '0 8px 24px rgba(var(--amber-rgb),0.25)' }}>{busy ? 'Marking…' : `Mark ${nChecked} watched`}</button>
          )}
        </div>
        <div style={{ color: 'var(--text-dim)', fontSize: 11.5, textAlign: 'center', marginTop: 10 }}>Anything you leave unchecked stays in Tonight.</div>
      </div>
    </Sheet>
  )
}
